import type { DocId, MemberRolesMap, Timestamp, UserId } from './common.js';

export type ExecutionType = 'manual' | 'automated' | 'hybrid';

/** React Flow node position in the DAG canvas. */
export interface NodePosition {
  x: number;
  y: number;
}

/**
 * `/projects/{projectId}/tasks/{taskId}` - task master record.
 *
 * Dependency graph semantics:
 *   - `dependsOn` lists direct parent task IDs within the same project.
 *   - The graph must be acyclic. See design-addendum.md §7 for the two-phase
 *     cycle-detection contract (client DFS + Cloud Function onWrite).
 *   - `cycleDetected` is set by the Cloud Function when a write would
 *     introduce a cycle; UI must surface a warning and block further writes
 *     that extend the cycle.
 */
export interface TaskDoc {
  taskId: DocId;
  projectId: string;
  /**
   * Stable source key for tasks imported from the migration xlsx (the
   * sheet's B-column "No", e.g. `"TT-0001"`). On a re-import the pipeline
   * matches an incoming row to its existing task by this key so edits land
   * on the same doc instead of creating duplicates. `null` for tasks
   * created by hand in the UI — those have no source row.
   */
  sourceTaskId: string | null;
  name: string;
  description: string;
  /**
   * Structured top-level category (the xlsx 大項目's leading segment,
   * e.g. `'事前作業'`, `'SMILE確認'`, `'その他'`). One of a small,
   * curated set surfaced as the list's カテゴリ column / filter chips and
   * the DAG node label. Hand-created tasks may leave this empty (`''` =
   * 未分類).
   */
  category: string;
  /**
   * Original 大項目 表記 (xlsx C-column) the {@link category} was derived
   * from, e.g. `'ＳＭＩＬＥマスタの有効期限調整'`. Kept verbatim so the
   * detail panel can show the source label beneath the structured
   * category. `null` for hand-created tasks that never carried a source
   * 大項目.
   */
  subcategory: string | null;
  /**
   * Reference into `/projects/{projectId}/assignees`. The single
   * canonical link from a migration task to its assignee. `null`
   * means the task is unassigned. Resolve the display name and
   * color through the matching `AssigneeDoc`.
   */
  assigneeId: DocId | null;
  /**
   * 領域 references for this task (multi-value). Each entry is a
   * `WbsCatalogOption.id` / label from the project's `wbsCatalog.area`
   * — the same catalog the WBS tree's 領域 cell consults. An empty
   * array means "未設定". This is the canonical field — all new code
   * reads and writes it. Mirrors Backlog's multi-category model.
   */
  areaIds: DocId[];
  /**
   * @deprecated Legacy single-value 領域. Kept only as a dual-read
   * fallback for docs not yet migrated to {@link areaIds}; the
   * `migrate:wbs-area-array` script deletes it. Never write this from
   * new code — read 領域 via {@link readTaskAreaIds}.
   */
  areaId?: DocId | null;
  /**
   * 移行元システム (sys From) — the source system a migration task moves
   * data OUT of, imported from the xlsx. Free text, optional. `null` /
   * absent for hand-created tasks or rows that never carried the column.
   * Surfaced as the「sys From」column in the task list.
   */
  sysFrom?: string | null;
  /**
   * 移行先システム (sys To) — the destination system a migration task
   * moves data INTO. Same import / optionality semantics as
   * {@link sysFrom}. Surfaced as the「sys To」column.
   */
  sysTo?: string | null;
  executionType: ExecutionType;
  estimatedMinutes: number;
  dependsOn: DocId[];
  position: NodePosition;

  /** Set to `true` by the onWrite cycle-detection trigger when a cycle is detected. */
  cycleDetected: boolean;
  /**
   * When `cycleDetected` is true, lists the task IDs participating in the
   * cycle so the UI can highlight them. Empty otherwise.
   */
  cycleMembers: DocId[];

  /**
   * Run-name labels this task applies to. Used by `generateExecutions` to
   * pre-mark executions as `skipped` for non-applicable runs.
   *
   * Matching rule: a task applies to a run if the run's `name` contains any
   * of these labels as a substring — e.g. label `"リハ"` matches runs named
   * `"移行リハ②"`, `"移行リハ③"`. Empty array means "applies to all runs".
   */
  applicableRunLabels: string[];

  /** Denormalized from parent project for Security Rules (see addendum §2). */
  memberUserIds: UserId[];
  memberRoles: MemberRolesMap;

  createdAt: Timestamp;
  updatedAt: Timestamp;
  createdByUserId: UserId;
  updatedByUserId: UserId;
}

/**
 * Dual-read accessor for a task's 領域 list.
 *
 * Prefers the canonical {@link TaskDoc.areaIds} array. Falls back to
 * interpreting the legacy {@link TaskDoc.areaId} as a single-element
 * list only when `areaIds` is entirely absent (a doc written before
 * the multi-value migration). A doc migrated to an empty list
 * (`areaIds: []`) reads as empty and does NOT resurrect the legacy
 * value.
 *
 * Pure; safe in render paths / `useMemo` deps.
 */
export function readTaskAreaIds(task: {
  areaIds?: readonly DocId[] | null;
  areaId?: DocId | null;
}): DocId[] {
  if (task.areaIds != null) return [...task.areaIds];
  const legacy = task.areaId;
  return legacy != null && legacy !== '' ? [legacy] : [];
}
